// Şub 28, 2026 — DPD KPI Kart Detay Dialog
// Her karta özel bağlamsal analiz: aylık trend + spesifik metrikler.
// kind: 'drivers' | 'parcels' | 'km' | 'retour' | 'rate' | 'start' | 'hours'
import { useEffect, useState, useMemo } from 'react';
import api from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, Package, Truck, Undo2, TrendingUp, Sunrise, Clock, Users, BarChart3, CalendarRange } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Bar, Line,
} from 'recharts';

const KIND_CONFIG = {
  drivers: {
    title: 'Şoför / Tur Aktivitesi',
    icon: Users,
    color: '#3b82f6',
    barKey: 'unique_couriers',
    barLabel: 'Aktif Şoför',
    lineKey: 'tour_count',
    lineLabel: 'Tur Adedi',
    focusMetric: (t) => t.unique_keys || 0,
    focusLabel: 'Bu dönem aktif',
  },
  parcels: {
    title: 'Paket Dağılımı',
    icon: Package,
    color: '#10b981',
    barKey: 'parcels',
    barLabel: 'Toplam Paket',
    lineKey: 'avg_parcels_per_day',
    lineLabel: 'Ort. Paket/Gün',
    focusMetric: (t) => (t.total_parcels || 0).toLocaleString(),
    focusLabel: 'Dönem toplamı',
  },
  km: {
    title: 'Km Trendi',
    icon: Truck,
    color: '#8b5cf6',
    barKey: 'km',
    barLabel: 'Toplam Km',
    lineKey: 'avg_km_per_day',
    lineLabel: 'Ort. Km/Gün',
    focusMetric: (t) => (t.total_km || 0).toLocaleString(),
    focusLabel: 'Dönem toplam km',
  },
  retour: {
    title: 'Retour Trendi',
    icon: Undo2,
    color: '#dc2626',
    barKey: 'retour',
    barLabel: 'Retour',
    lineKey: 'delivery_rate',
    lineLabel: 'Teslim %',
    focusMetric: (t) => (t.total_retour || 0).toLocaleString(),
    focusLabel: 'Dönem retour toplamı',
  },
  rate: {
    title: 'Teslim Oranı Trendi',
    icon: TrendingUp,
    color: '#f97316',
    barKey: 'parcels',
    barLabel: 'Paket',
    lineKey: 'delivery_rate',
    lineLabel: 'Teslim %',
    focusMetric: (t) => `%${t.overall_delivery_rate || 0}`,
    focusLabel: 'Dönem ortalaması',
  },
  start: {
    title: 'Başlangıç Saati Trendi',
    icon: Sunrise,
    color: '#f59e0b',
    barKey: 'tour_count',
    barLabel: 'Tur',
    lineKey: 'avg_start_minutes',
    lineLabel: 'Ort. Başlangıç (dk)',
    focusMetric: (t) => t.overall_avg_start_time || '-',
    focusLabel: 'Dönem ortalaması',
  },
  hours: {
    title: 'Ort. Çalışma Saati Trendi',
    icon: Clock,
    color: '#6366f1',
    barKey: 'tour_count',
    barLabel: 'Tur',
    lineKey: 'avg_hours',
    lineLabel: 'Ort. Saat',
    focusMetric: (t) => t.overall_avg_working_hours ? `${t.overall_avg_working_hours}h` : '-',
    focusLabel: 'Dönem ortalaması',
  },
};

function hhmmToMin(s) {
  if (!s || typeof s !== 'string') return null;
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}
function minToHhmm(m) {
  if (m == null) return '-';
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(Math.round(m % 60)).padStart(2, '0')}`;
}

export default function DPDPerfKpiDialog({ open, onClose, kind, firmId, totals, groupBy }) {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.parcels;
  const Icon = cfg.icon;
  const [year, setYear] = useState('__all__');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !firmId) return;
    setLoading(true);
    const params = new URLSearchParams({ firm_id: firmId });
    if (year !== '__all__') params.append('year', year);
    api.get(`/dpd/monthly-workhours?${params.toString()}`)
      .then((r) => setData(r.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, firmId, year]);

  const chartData = useMemo(() => {
    return (data?.series || []).map((s) => ({
      ...s,
      avg_start_minutes: hhmmToMin(s.avg_start_time),
    }));
  }, [data]);

  const kindStats = useMemo(() => {
    if (!chartData.length) return { best: null, worst: null, avg: null };
    const key = cfg.lineKey === 'avg_start_minutes' ? 'avg_start_minutes' : cfg.lineKey;
    const withVal = chartData.filter((r) => r[key] != null);
    if (!withVal.length) return { best: null, worst: null, avg: null };
    const sorted = [...withVal].sort((a, b) => (a[key] || 0) - (b[key] || 0));
    const avg = withVal.reduce((s, r) => s + (r[key] || 0), 0) / withVal.length;
    return {
      best: sorted[sorted.length - 1],
      worst: sorted[0],
      avg,
      lineKey: key,
    };
  }, [chartData, cfg]);

  const formatVal = (v) => {
    if (v == null) return '-';
    if (cfg.lineKey === 'avg_start_minutes') return minToHhmm(v);
    if (cfg.lineKey === 'delivery_rate') return `%${v}`;
    if (cfg.lineKey === 'avg_hours') return `${v}h`;
    return typeof v === 'number' ? v.toLocaleString() : v;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="perf-kpi-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: cfg.color }} />
            {cfg.title}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between flex-wrap gap-2">
            <span>
              {totals.days_range} — {totals.days_count || 0} çalışma günü / {totals.unique_keys || 0} {groupBy === 'courier' ? 'şoför' : 'tur'}
            </span>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-7 w-[130px] text-xs" data-testid="perf-detail-year">
                <SelectValue placeholder="Yıl" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Yıllar</SelectItem>
                {(data?.available_years || []).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogDescription>
        </DialogHeader>

        {/* Focus KPI */}
        <div className="rounded-md p-4 border" style={{ borderColor: cfg.color + '40', background: cfg.color + '0d' }}>
          <div className="text-[11px] uppercase tracking-wide" style={{ color: cfg.color }}>{cfg.focusLabel}</div>
          <div className="text-3xl font-bold mt-1" style={{ color: cfg.color }}>{cfg.focusMetric(totals)}</div>
        </div>

        {/* Chart */}
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <CalendarRange className="h-3 w-3" /> {year === '__all__' ? 'Yıllar bazında' : `${year} aylık dağılım`}
          </div>
          {loading ? (
            <div className="h-56 flex items-center justify-center text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-gray-400">Veri yok</div>
          ) : (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v, k) => {
                      if (k === cfg.lineLabel && cfg.lineKey === 'avg_start_minutes') return [minToHhmm(v), k];
                      if (k === cfg.lineLabel && cfg.lineKey === 'delivery_rate') return [`%${v}`, k];
                      if (k === cfg.lineLabel && cfg.lineKey === 'avg_hours') return [`${v}h`, k];
                      return [typeof v === 'number' ? v.toLocaleString() : v, k];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey={cfg.barKey} name={cfg.barLabel} fill={cfg.color + 'cc'} radius={[3,3,0,0]} barSize={26} />
                  <Line yAxisId="right" type="monotone" dataKey={cfg.lineKey === 'avg_start_minutes' ? 'avg_start_minutes' : cfg.lineKey} name={cfg.lineLabel} stroke={cfg.color} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Kind-specific stats */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <MiniStat label="En Yüksek" value={kindStats.best ? `${formatVal(kindStats.best[kindStats.lineKey])}` : '-'} hint={kindStats.best?.period} accent={cfg.color} />
          <MiniStat label="Ortalama" value={kindStats.avg != null ? formatVal(kindStats.avg) : '-'} hint={`${chartData.length} dönem`} />
          <MiniStat label="En Düşük" value={kindStats.worst ? `${formatVal(kindStats.worst[kindStats.lineKey])}` : '-'} hint={kindStats.worst?.period} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, hint, accent }) {
  return (
    <div className="rounded-md border p-2 bg-gray-50 border-gray-200">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-lg font-semibold" style={accent ? { color: accent } : undefined}>{value}</div>
      {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}
